/** @jsx jsx */

import type { CSSProperties, ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { buildDataAttributes, jsx } from '@balance-web/core';
import { useTheme } from '@balance-web/theme';

import { siftAriaAttributes } from '../utils';

export type TableGroupHeaderProps = UseGroupHeaderStylesProps & {
  'aria-colspan'?: number;
  children: ReactNode;
};

export const TableGroupHeader = forwardRef<
  HTMLDivElement,
  TableGroupHeaderProps
>(({ children, ...props }, ref) => {
  const { css, dataAttributes, style } = useGroupHeaderStyles(props);
  const ariaAttributes = siftAriaAttributes(props);

  return (
    <div ref={ref} role="row">
      <div
        css={css}
        role="columnheader"
        style={style}
        {...ariaAttributes}
        {...dataAttributes}
      >
        {children}
      </div>
    </div>
  );
});

// Styles
// ------------------------------

type UseGroupHeaderStylesProps = {
  /**
   * Sets inline [style](https://reactjs.org/docs/dom-elements.html#style) for
   * the element. Only use as a **last resort**.
   */
  UNSAFE_style?: CSSProperties;
};

export function useGroupHeaderStyles(props: UseGroupHeaderStylesProps) {
  const { borderWidth, semantic, spacing } = useTheme();
  const { UNSAFE_style } = props;

  // dynamic properties
  const style = useMemo(() => {
    return UNSAFE_style;
  }, [UNSAFE_style]);

  // static-ish properties
  const dataAttributes = useMemo(() => {
    return buildDataAttributes({ type: 'group' }, 'rowgroup');
  }, []);

  // declarations
  const css = useMemo(() => {
    return [
      {
        backgroundColor: semantic.colour.surface.neutral.background1,
        borderBottom: `${borderWidth.standard} solid ${semantic.colour.surface.neutral.border}`,
        minWidth: 0, // resolve flex issues with text trucation
        padding: spacing.medium,
      } as const,
    ];
  }, [
    borderWidth.standard,
    semantic.colour.surface.neutral.background1,
    semantic.colour.surface.neutral.border,
    spacing.medium,
  ]);

  return { dataAttributes, css, style };
}
